"use client"

import React, { useState, useEffect } from 'react'
import { UserAvailability, MeetingDur } from '../../../types/availability'
import { loadUserAvailability, saveUserAvailability } from '../../../lib/availability.service'
import TimezoneSelect from '../../availability/TimezoneSelect'
import ChannelToggles from '../../availability/ChannelToggles'
import AvailabilityEditor from '../../availability/AvailabilityEditor'

interface AvailabilityStepProps {
  uid: string
  onNext: () => void
  onBack: () => void
}

export default function AvailabilityStep({ uid, onNext, onBack }: AvailabilityStepProps) {
  const [loading, setLoading] = useState(true)
  // removed saving state (unused)
  const [availability, setAvailability] = useState<Partial<UserAvailability>>({
    tz: 'Europe/Amsterdam',
    callEnabled: true,
    videoEnabled: true,
    windows: [],
    defaultDur: 30
  })
  const [chatOnly, setChatOnly] = useState(false)

  useEffect(() => {
    loadAvailability()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  const loadAvailability = async () => {
    setLoading(true)
    try {
      const userAvailability = await loadUserAvailability(uid)
      if (userAvailability) {
        setAvailability(userAvailability)
        setChatOnly(!userAvailability.callEnabled && !userAvailability.videoEnabled)
      }
    } catch (error) {
      console.error('Error loading availability:', error)
    } finally {
      setLoading(false)
    }
  }

  // Auto-save when data changes
  React.useEffect(() => {
    if (uid && !loading) {
      saveUserAvailability(uid, availability).catch(error => {
        console.error('Error auto-saving availability:', error)
      })
    }
  }, [availability, uid, loading])

  const handleTimezoneChange = (timezone: string) => {
    setAvailability(prev => ({ ...prev, tz: timezone }))
  }

  const handleCallToggle = (enabled: boolean) => {
    setAvailability(prev => ({ ...prev, callEnabled: enabled }))
    if (!enabled && !availability.videoEnabled) {
      setChatOnly(true)
    } else {
      setChatOnly(false)
    }
  }

  const handleVideoToggle = (enabled: boolean) => {
    setAvailability(prev => ({ ...prev, videoEnabled: enabled }))
    if (!enabled && !availability.callEnabled) {
      setChatOnly(true)
    } else {
      setChatOnly(false)
    }
  }

  const handleDurationChange = (duration: MeetingDur) => {
    setAvailability(prev => ({ ...prev, defaultDur: duration }))
  }

  const handleWindowsChange = (windows: any[]) => {
    setAvailability(prev => ({ ...prev, windows }))
  }

  const handleChatOnlyToggle = (enabled: boolean) => {
    setChatOnly(enabled)
    if (enabled) {
      setAvailability(prev => ({ 
        ...prev, 
        callEnabled: false, 
        videoEnabled: false,
        windows: []
      }))
    } else {
      setAvailability(prev => ({ 
        ...prev, 
        callEnabled: true, 
        videoEnabled: true 
      }))
    }
  }

  // Check if user can proceed
  const canProceed = chatOnly || (availability.windows && availability.windows.length > 0)

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="space-y-4">
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Beschikbaarheid</h2>
        <p className="text-gray-600">Stel in wanneer je beschikbaar bent voor gesprekken.</p>
      </div>

      {/* Chat Only Option */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="chatOnly"
            checked={chatOnly}
            onChange={(e) => handleChatOnlyToggle(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="chatOnly" className="text-sm font-medium text-gray-700">
            Alleen chat beschikbaar (geen gesprekken)
          </label>
        </div>
        {chatOnly && (
          <p className="text-xs text-gray-500 mt-2">
            Leden kunnen je alleen via chat bereiken. Je kunt dit later altijd aanpassen in de instellingen.
          </p>
        )}
      </div>

      {!chatOnly && (
        <>
          {/* Timezone Section */}
          <div className="bg-white rounded-lg border p-6">
            <TimezoneSelect
              value={availability.tz || 'Europe/Amsterdam'}
              onChange={handleTimezoneChange}
            />
          </div>

          {/* Channels Section */}
          <div className="bg-white rounded-lg border p-6">
            <ChannelToggles
              callEnabled={availability.callEnabled ?? true}
              videoEnabled={availability.videoEnabled ?? true}
              defaultDur={availability.defaultDur || 30}
              onCallToggle={handleCallToggle}
              onVideoToggle={handleVideoToggle}
              onDurationChange={handleDurationChange}
            />
          </div>

          {/* Availability Editor Section */}
          <div className="bg-white rounded-lg border p-6">
            <AvailabilityEditor
              windows={availability.windows || []}
              onChange={handleWindowsChange}
            />
          </div>
        </>
      )}

      {/* Note: Navigation handled by parent OnboardingWizard */}

      {/* Help text */}
      {!canProceed && !chatOnly && (
        <div className="text-center text-sm text-gray-500">
          Selecteer minimaal één beschikbaarheidsvenster of kies "Alleen chat beschikbaar"
        </div>
      )}
    </div>
  )
}
